import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  Box,
  Divider,
  Grid,
  Button,
  Avatar,
  Tab,
  Tabs,
  useTheme,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import {
  FilterChip,
  QuickSearch,
  useAPI,
  dateFormatter,
  VerticalBoxContainer,
  PageContentWrapper,
} from '@shared/components';
import axios from '../../../../utils/axios';
import { handleChipDelete } from '../../../../utils/filterHelpers';
import ExcelExportButton from '../../../../components/ExcelExportButton';
import { handleSortChange, getSortDirection } from '../../../../utils/sortHelpers';
import Table from './Table';
import Filter from './Filter';
import AttributeListModal from './TableModal';

const fields = [
  {
    label: 'ID',
    value: 'id',
  },
  {
    label: 'Atribut (Metapodatak)',
    value: 'attributeName',
  },
  {
    label: 'Vrijednost',
    value: 'valueDisplayed',
  },
  {
    label: 'Datum kreiranja',
    value: 'dateCreated',
    type: 'dateTime',
  },
  {
    label: 'Korisnik kreirao',
    value: 'createdBy',
  },
];

const getLabelText = (value) => {
  const type = {
    id: 'ID',
    attributeName: 'Atribut (Metpodatak)',
    valueDisplayed: 'Vrijednost',
    dateCreated: 'Datum kreiranja',
    createdBy: 'Korisnik kreirao',
  };
  return type[value];
};

const getChipValue = (chip, filters) => {
  if (chip === 'dateCreated') return dateFormatter(filters[chip]);
  return filters[chip];
};

const DocumentDetails = ({ documentId, documentLoading }) => {
  const [page, setPage] = useState(0);
  const theme = useTheme();
  const [filters, setFilters] = useState({});
  const [triggerRerender, setRerender] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(35);
  const [sorters, setSorters] = useState([]);
  const [journalModalVisible, setJournalModalVisible] = useState(false);

  const getRandomColorById = (id) => {
    const colors = theme.palette.chart_colors;
    return colors[id % colors.length];
  };
  const [attributes = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      !documentLoading
        ? axios
            .get(`document-attributes`, {
              params: {
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                conditions: {
                  ...filters,
                  document: documentId,
                  ...(selectedGroup !== 'all' && { attributeGroupId: selectedGroup }),
                },
                orderBy: [...sorters, '"ordNumber" asc'],
              },
            })
            .then(({ data }) => {
              return data;
            })
        : Promise.resolve(() => {}),
    [page, rowsPerPage, filters, sorters, triggerRerender, documentLoading, selectedGroup],
  );

  const [attributeGroups = { nodes: [] }] = useAPI(
    () =>
      !documentLoading
        ? axios.get(`attribute-groups/${documentId}`).then(({ data }) => data)
        : Promise.resolve(() => {}),
    [triggerRerender, documentLoading],
  );

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        setFilters={setFilters}
        setPage={setPage}
        getLabelText={getLabelText}
        getChipValue={getChipValue}
      />
      <AttributeListModal
        documentId={documentId}
        visible={journalModalVisible}
        setVisible={setJournalModalVisible}
        title="Journal promjena atributa"
        buttonText="Odustani"
      />
      {/* <PageHeader
        title={`Dokument ${document?.id || ''}`}
        subtitle="Detalji dokumenta"
        sub
        showBackIcon
        history={history}
      /> */}
      <PageContentWrapper style={{ paddingTop: '4px', paddingRight: '2px', paddingLeft: '2px' }}>
        <Box
          width="100%"
          bgcolor="background.paper"
          borderRadius="2px 2px 0"
          border={`1px solid ${theme.palette.divider}`}
          borderBottom={0}
          marginLeft="-1px"
        >
          <Tabs
            value={selectedGroup}
            onChange={(e, value) => setSelectedGroup(value)}
            indicatorColor="primary"
            aria-label="attribute group tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              style={{
                minWidth: '0',
              }}
              value="all"
              label={
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  justifyContent="center"
                  gridGap={5}
                >
                  <Avatar
                    variant="rounded"
                    style={{
                      backgroundColor: getRandomColorById('all'),
                      width: '1.5rem',
                      height: '1.5rem',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}
                  >
                    {attributeGroups.nodes.reduce(
                      (acc, group) => acc + Number(group.attributeCount),
                      0,
                    )}
                  </Avatar>
                  Svi atributi
                </Box>
              }
            />
            {attributeGroups.nodes.map((group) => (
              <Tab
                key={group.id}
                value={group.id}
                style={
                  attributeGroups.nodes.length < 8
                    ? {
                        minWidth: '0',
                      }
                    : {}
                }
                label={
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="row"
                    justifyContent="center"
                    gridGap={5}
                  >
                    <Avatar
                      variant="rounded"
                      style={{
                        backgroundColor: getRandomColorById(group.id),
                        width: '1.5rem',
                        height: '1.5rem',
                        fontSize: '0.75rem',
                      }}
                    >
                      {group.attributeCount || 0}
                    </Avatar>
                    {group.name}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
        <Card
          style={{
            borderTopLeftRadius: '0',
            borderTopRightRadius: '0',
          }}
        >
          <CardHeader
            title={
              <QuickSearch
                name="attributeName"
                placeholder="Brza pretraga po nazivu atributa"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={attributes.totalCount}
                    fields={fields}
                    sorters={[...sorters, '"ordNumber" asc']}
                    filters={{ ...filters, document: documentId }}
                    routeName="document-attributes"
                    variant="outlined"
                    fileName={`atributi-dokumenta-${documentId}`}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<LibraryBooksIcon fontSize="small" />}
                    onClick={() => setJournalModalVisible(true)}
                  >
                    <Box fontSize="small">Journal promjena</Box>
                  </Button>
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <Table
            loading={loading || documentLoading}
            error={error}
            attributes={attributes.nodes}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={attributes.totalCount}
            handleSortChange={handleSortChange}
            getSortDirection={getSortDirection}
            setSorters={setSorters}
            sorters={sorters}
            setRerender={setRerender}
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default DocumentDetails;
